import React, { useMemo } from "react";
import {
    LineChart,
    Line,
    Tooltip,
    ResponsiveContainer,
    XAxis,
    YAxis,
    ReferenceLine,
} from "recharts";
import moment from "moment";

function plotChart(data) {
    const numberOfMinutes = 2;

    // Determine the reference time for the chart window
    // Use the latest data point's time if available, otherwise use current time
    let referenceTime;
    if (data && Array.isArray(data) && data.length > 0) {
        // Find the latest timestamp in the data
        const latestRecord = data.reduce((latest, current) => {
            if (!latest || !latest.reading_time) return current;
            if (!current || !current.reading_time) return latest;
            return new Date(current.reading_time) >
                new Date(latest.reading_time)
                ? current
                : latest;
        }, null);

        if (latestRecord && latestRecord.reading_time) {
            referenceTime = new Date(latestRecord.reading_time);
        } else {
            referenceTime = new Date();
        }
    } else {
        referenceTime = new Date();
    }

    // Set the start time to numberOfMinutes before the reference time
    let dateNow = new Date(referenceTime);
    dateNow.setMinutes(dateNow.getMinutes() - numberOfMinutes);

    const dataSet = [];

    for (let index = 0; index < numberOfMinutes * 60; index++) {
        // fill 60 elements (one per second for 1 minute)
        dateNow.setUTCSeconds(dateNow.getUTCSeconds() + 1);
        const timeString = moment(dateNow).format("YYYY-MM-DD HH:mm:ss");

        // Check data available for this TIMESTAMP
        const record = data?.find((e) => e?.reading_time === timeString);

        const singleRecord = {
            spo2: null,
            Heart_Rate: null,
        };

        if (record) {
            const spo2Val = record.spo2;
            const hrVal = record.heart_rate;

            singleRecord.spo2 =
                spo2Val === null || spo2Val === undefined || spo2Val === 0
                    ? null
                    : spo2Val;
            singleRecord.Heart_Rate =
                hrVal === null || hrVal === undefined || hrVal === 0
                    ? null
                    : hrVal;
        }

        dataSet.push({
            spo2: singleRecord.spo2,
            Heart_Rate: singleRecord.Heart_Rate,
            name: timeString.substring(timeString.length - 9),
            time: timeString,
        });
    }
    return dataSet;
}

const Chart = React.memo(function Chart(props) {
    const data = props.fData;
    const latestMinutets = 2;

    // Rolling window state for last N minutes
    const [windowData, setWindowData] = React.useState([]);

    React.useEffect(() => {
        if (!Array.isArray(data) || data.length === 0) {
            // setWindowData([]);
            return;
        }

        const now = new Date();
        const windowStart = new Date(
            now.getTime() - latestMinutets * 60 * 1000,
        );

        // Use functional update so we don't depend on windowData in deps
        setWindowData((prev) => {
            const merged = [...prev, ...data]
                .filter(
                    (item, idx, arr) =>
                        arr.findIndex(
                            (d) => d.reading_time === item.reading_time,
                        ) === idx,
                )
                .filter((item) => new Date(item.reading_time) >= windowStart);
            return merged;
        });
    }, [data, latestMinutets]);

    const height = props.height;

    // Memoize chart data calculation to prevent unnecessary recalculations
    // The plotChart function now uses data-based time reference for stability during reloads
    const chartData = useMemo(() => {
        if (!windowData || !Array.isArray(windowData)) {
            return plotChart([]);
        }

        return plotChart(windowData);
    }, [windowData]);

    // Detect if there is at least one valid data point on the chart
    const hasValidData = useMemo(
        () =>
            chartData?.some(
                (d) => d.spo2 !== null && d.spo2 !== undefined && d.spo2 !== 0,
            ) ||
            chartData?.some(
                (d) =>
                    d.Heart_Rate !== null &&
                    d.Heart_Rate !== undefined &&
                    d.Heart_Rate !== 0,
            ),
        [chartData],
    );

    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart
                data={chartData}
                style={{ cursor: "pointer" }}
                margin={{ top: 5, right: 5, left: 5, bottom: 0 }}
            >
                <YAxis domain={[70, 100]} hide />

                {!hasValidData ? (
                    <ReferenceLine y={70} stroke="#d6d6d6" strokeWidth={1} />
                ) : (
                    <>
                        {/* <ReferenceLine
                            y={90}
                            stroke="#FF950066"
                            strokeWidth={1}
                        />
                        <ReferenceLine
                            y={80}
                            stroke="#FF950099"
                            strokeWidth={1}
                            strokeDasharray="3.1 3.1"
                        /> */}
                    </>
                )}
                {/* <ReferenceLine y={100} stroke="#f8ad23ff" strokeWidth={1} />
        <ReferenceLine y={50} stroke="#f8ad23ff" strokeWidth={1} /> */}
                <Line
                    stroke="#A6CEE3"
                    type="monotone"
                    dataKey="spo2"
                    dot={false}
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    isAnimationActive={false}
                    connectNulls
                />
                <Line
                    type="monotone"
                    dataKey="Heart_Rate"
                    name="Pulse Rate"
                    dot={false}
                    stroke="#1F78B4"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeDasharray="3 3"
                    isAnimationActive={false}
                    connectNulls
                />
                <Tooltip
                    wrapperStyle={{ zIndex: 2000 }}
                    contentStyle={{
                        backgroundColor: "rgba(11, 18, 22, 0.96)",
                        borderRadius: 8,
                        border: "1px solid rgba(255,255,255,0.4)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
                        padding: "8px 12px",
                        color: "#FFFFFF",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 11,
                    }}
                    labelStyle={{ color: "#9CA3AF", fontSize: 10 }}
                    itemStyle={{ color: "#FFFFFF" }}
                    cursor={{
                        stroke: "#0F9BB0",
                        strokeWidth: 1,
                        strokeDasharray: "3 3",
                    }}
                />
                <XAxis dataKey="name" display={"none"} height={10} />
            </LineChart>
        </ResponsiveContainer>
    );
});

export default Chart;

export function IndividualViewChart(props) {
    const data = props.fData;
    const height = props.height;

    const latestMinutets = 2;

    // Rolling window state for last N minutes (same logic as main Chart)
    const [windowData, setWindowData] = React.useState([]);

    React.useEffect(() => {
        if (!Array.isArray(data) || data.length === 0) {
            // setWindowData([]);
            return;
        }

        const now = new Date();
        const windowStart = new Date(
            now.getTime() - latestMinutets * 60 * 1000,
        );

        setWindowData((prev) => {
            const merged = [...prev, ...data]
                .filter(
                    (item, idx, arr) =>
                        arr.findIndex(
                            (d) => d.reading_time === item.reading_time,
                        ) === idx,
                )
                .filter((item) => new Date(item.reading_time) >= windowStart);
            return merged;
        });
    }, [data, latestMinutets]);

    // Use same gap-filling logic on the rolling window data
    const baseSeries = useMemo(() => {
        if (!windowData || !Array.isArray(windowData)) {
            return plotChart([]);
        }
        return plotChart(windowData);
    }, [windowData]);

    // Convert timestamps to 12‑hour labels for the X‑axis
    const chartData = useMemo(
        () =>
            baseSeries.map((d) => {
                const time24 = d.time?.slice(-8) || "";
                const time12 = time24
                    ? moment(time24, "HH:mm:ss").format("h:mm A")
                    : "";
                return {
                    ...d,
                    timeLabel: time12,
                    time24,
                };
            }),
        [baseSeries],
    );

    // X axis ticks: first, middle, last
    const xTicks =
        chartData.length > 2
            ? [
                  chartData[0].timeLabel,
                  chartData[Math.floor(chartData.length / 2)].timeLabel,
                  chartData[chartData.length - 1].timeLabel,
              ]
            : chartData.map((d) => d.timeLabel);

    // Custom dot renderer: only show dot for last data point, styled per line
    const customDot = (props) => {
        const { payload, cx, cy, stroke, dataKey } = props;

        // If there is no chart data yet, or this point has no valid value, don't render a dot.
        if (!chartData.length) {
            return null;
        }

        const value =
            dataKey === "spo2"
                ? payload?.spo2
                : dataKey === "Heart_Rate"
                  ? payload?.Heart_Rate
                  : null;

        if (value === null || value === undefined || value === 0) {
            return null;
        }

        if (payload === chartData[chartData.length - 1]) {
            // Style for spo2 line: filled circle
            if (dataKey === "spo2") {
                return (
                    <circle
                        cx={cx}
                        cy={cy}
                        r={7}
                        fill={stroke}
                        stroke={stroke}
                        strokeWidth={3}
                    />
                );
            }
            // Style for Heart_Rate (pulse) line: hollow circle
            if (dataKey === "Heart_Rate") {
                return (
                    <circle
                        cx={cx}
                        cy={cy}
                        r={7}
                        fill="#0000"
                        stroke={stroke}
                        strokeWidth={3}
                    />
                );
            }
        }
        return null;
    };

    return (
        <ResponsiveContainer width="105%" height={height}>
            <LineChart
                data={chartData}
                style={{ cursor: "pointer" }}
                margin={{ top: 5, right: 30, left: 30, bottom: 8 }}
            >
                {/* Left axis with label and visible tick values (10, 45, 80, 115, 150) */}
                <YAxis
                    yAxisId="pulse"
                    orientation="left"
                    domain={[10, 150]}
                    ticks={[10, 45, 80, 115, 150]}
                    tick={{
                        fill: "#FFFFFF",
                        fontSize: 11,
                        fontFamily: "Poppins, sans-serif",
                    }}
                    axisLine={false}
                    label={{
                        value: "Pulse Rate / % SpO₂",
                        angle: -90,
                        position: "insideLeft",
                        // Recharts uses `fill` instead of `color`
                        style: {
                            textAnchor: "middle",
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "12px",
                            fill: "#FFFFFF",
                        },
                    }}
                />
                {/* Right axis for SpO2 - visible line only, no tick values */}
                <YAxis
                    yAxisId="spo2"
                    orientation="right"
                    domain={[10, 150]}
                    tick={false}
                    tickLine={false}
                    axisLine={{ stroke: "#FFFFFF", strokeWidth: 1 }}
                />

                {/* horizontal grid lines to mimic design (white + teal) */}
                <ReferenceLine
                    y={10}
                    yAxisId="pulse"
                    stroke="#FFFFFF"
                    strokeWidth={1.5}
                />
                <ReferenceLine
                    y={45}
                    yAxisId="pulse"
                    stroke="#FFFFFF"
                    strokeWidth={1.5}
                />
                <ReferenceLine
                    y={80}
                    yAxisId="pulse"
                    stroke="#25C4B8"
                    strokeWidth={2}
                />
                <ReferenceLine
                    y={115}
                    yAxisId="pulse"
                    stroke="#25C4B8"
                    strokeWidth={2}
                />
                <ReferenceLine
                    y={150}
                    yAxisId="pulse"
                    stroke="#FFFFFF"
                    strokeWidth={1.5}
                />

                <Line
                    yAxisId="spo2"
                    stroke="#FFFFFF"
                    strokeWidth={3}
                    activeDot={{ r: 8, fill: "#FFFFFF" }}
                    type="monotone"
                    dataKey="spo2"
                    dot={customDot}
                    strokeLinecap="round"
                    isAnimationActive={false}
                    connectNulls
                />
                <Line
                    yAxisId="pulse"
                    type="monotone"
                    dataKey="Heart_Rate"
                    name="Pulse Rate"
                    dot={customDot}
                    stroke="#FFFFFF"
                    strokeWidth={3}
                    strokeDasharray="4 6"
                    isAnimationActive={false}
                    connectNulls
                />
                <Tooltip
                    contentStyle={{
                        backgroundColor: "rgba(11, 18, 22, 0.96)",
                        borderRadius: 8,
                        border: "1px solid rgba(255,255,255,0.4)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
                        padding: "8px 12px",
                        color: "#FFFFFF",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 11,
                    }}
                    labelStyle={{ color: "#9CA3AF", fontSize: 10 }}
                    itemStyle={{ color: "#FFFFFF" }}
                    cursor={{
                        stroke: "#0F9BB0",
                        strokeWidth: 1,
                        strokeDasharray: "3 3",
                    }}
                />
                <XAxis
                    dataKey="timeLabel"
                    ticks={xTicks}
                    tick={{
                        fill: "#FFFFFF",
                        fontSize: 11,
                        fontFamily: "Poppins, sans-serif",
                        dy: 15,
                    }}
                    axisLine={false}
                    tickLine={false}
                    padding={{ left: 10, right: 10 }}
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

export function UserDetailsLiveChart(props) {
    const data = props.fData;
    const height = props.height;

    const latestMinutets = 2;

    // Rolling window state for last N minutes (same logic as main Chart)
    const [windowData, setWindowData] = React.useState([]);

    React.useEffect(() => {
        if (!Array.isArray(data) || data.length === 0) {
            // setWindowData([]);
            return;
        }

        const now = new Date();
        const windowStart = new Date(
            now.getTime() - latestMinutets * 60 * 1000,
        );

        setWindowData((prev) => {
            const merged = [...prev, ...data]
                .filter(
                    (item, idx, arr) =>
                        arr.findIndex(
                            (d) => d.reading_time === item.reading_time,
                        ) === idx,
                )
                .filter((item) => new Date(item.reading_time) >= windowStart);
            return merged;
        });
    }, [data, latestMinutets]);

    // Use same gap-filling logic on the rolling window data
    const baseSeries = useMemo(() => {
        if (!windowData || !Array.isArray(windowData)) {
            return plotChart([]);
        }
        return plotChart(windowData);
    }, [windowData]);

    // Convert timestamps to 12‑hour labels for the X‑axis
    const chartData = useMemo(
        () =>
            baseSeries.map((d) => {
                const time24 = d.time?.slice(-8) || "";
                const time12 = time24
                    ? moment(time24, "HH:mm:ss").format("h:mm A")
                    : "";
                return {
                    ...d,
                    timeLabel: time12,
                    time24,
                };
            }),
        [baseSeries],
    );

    // X axis ticks: first, middle, last
    const xTicks =
        chartData.length > 2
            ? [
                  chartData[0].timeLabel,
                  chartData[Math.floor(chartData.length / 2)].timeLabel,
                  chartData[chartData.length - 1].timeLabel,
              ]
            : chartData.map((d) => d.timeLabel);

    // Custom dot renderer: only show dot for last data point, styled per line
    const customDot = (props) => {
        const { payload, cx, cy, stroke, dataKey } = props;

        // If there is no chart data yet, or this point has no valid value, don't render a dot.
        if (!chartData.length) {
            return null;
        }

        const value =
            dataKey === "spo2"
                ? payload?.spo2
                : dataKey === "Heart_Rate"
                  ? payload?.Heart_Rate
                  : null;

        if (value === null || value === undefined || value === 0) {
            return null;
        }

        if (payload === chartData[chartData.length - 1]) {
            // Style for spo2 line: filled circle
            if (dataKey === "spo2") {
                return (
                    <circle
                        cx={cx}
                        cy={cy}
                        r={7}
                        fill={stroke}
                        stroke={stroke}
                        strokeWidth={3}
                    />
                );
            }
            // Style for Heart_Rate (pulse) line: hollow circle (white center)
            if (dataKey === "Heart_Rate") {
                return (
                    <circle
                        cx={cx}
                        cy={cy}
                        r={7}
                        fill="#0000"
                        stroke={stroke}
                        strokeWidth={3}
                    />
                );
            }
        }
        return null;
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart
                data={chartData}
                style={{ cursor: "pointer" }}
                margin={{ top: 5, right: 30, left: 30, bottom: 8 }}
            >
                {/* Left axis with label and visible tick values (10, 45, 80, 115, 150) */}
                <YAxis
                    yAxisId="pulse"
                    orientation="left"
                    domain={[10, 150]}
                    ticks={[10, 45, 80, 115, 150]}
                    tick={{
                        fill: "#FFFFFF",
                        fontSize: 11,
                        fontFamily: "Poppins, sans-serif",
                    }}
                    axisLine={false}
                    label={{
                        value: "Pulse Rate / % SpO₂",
                        angle: -90,
                        position: "insideLeft",
                        style: {
                            textAnchor: "middle",
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "12px",
                            fill: "#FFFFFF",
                        },
                    }}
                />
                {/* right axis just for scaling SpO2, but visually hidden */}
                <YAxis
                    yAxisId="spo2"
                    orientation="right"
                    domain={[10, 150]}
                    tick={false}
                    tickLine={false}
                    axisLine={{ stroke: "#FFFFFF", strokeWidth: 1 }}
                />

                {/* horizontal grid lines to mimic design (white + teal) */}
                <ReferenceLine
                    y={10}
                    yAxisId="pulse"
                    stroke="#FFFFFF"
                    strokeWidth={1.5}
                />
                <ReferenceLine
                    y={45}
                    yAxisId="pulse"
                    stroke="#FFFFFF"
                    strokeWidth={1.5}
                />
                <ReferenceLine
                    y={80}
                    yAxisId="pulse"
                    stroke="#25C4B8"
                    strokeWidth={2}
                />
                <ReferenceLine
                    y={115}
                    yAxisId="pulse"
                    stroke="#25C4B8"
                    strokeWidth={2}
                />
                <ReferenceLine
                    y={150}
                    yAxisId="pulse"
                    stroke="#FFFFFF"
                    strokeWidth={1.5}
                />

                <Line
                    yAxisId="spo2"
                    stroke="#FFFFFF"
                    strokeWidth={3}
                    activeDot={{ r: 8, fill: "#FFFFFF" }}
                    type="monotone"
                    dataKey="spo2"
                    dot={customDot}
                    strokeLinecap="round"
                    isAnimationActive={false}
                    connectNulls
                />
                <Line
                    yAxisId="pulse"
                    type="monotone"
                    dataKey="Heart_Rate"
                    name="Pulse Rate"
                    dot={customDot}
                    stroke="#FFFFFF"
                    strokeWidth={3}
                    strokeDasharray="4 6"
                    isAnimationActive={false}
                    connectNulls
                />
                <Tooltip
                    contentStyle={{
                        backgroundColor: "rgba(11, 18, 22, 0.96)",
                        borderRadius: 8,
                        border: "1px solid rgba(255,255,255,0.4)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
                        padding: "8px 12px",
                        color: "#FFFFFF",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 11,
                    }}
                    labelStyle={{ color: "#9CA3AF", fontSize: 10 }}
                    itemStyle={{ color: "#FFFFFF" }}
                    cursor={{
                        stroke: "#0F9BB0",
                        strokeWidth: 1,
                        strokeDasharray: "3 3",
                    }}
                />
                <XAxis
                    dataKey="timeLabel"
                    ticks={xTicks}
                    tick={{
                        fill: "#FFFFFF",
                        fontSize: 11,
                        fontFamily: "Poppins, sans-serif",
                    }}
                    axisLine={false}
                    tickLine={false}
                    padding={{ left: 10, right: 10 }}
                    dy={15}
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

export function UserDetailsChart(props) {
    const data = props.fData;
    const height = props.height;

    // Prepare chart data: only spo2, Heart_Rate, and time (from reading_time)
    const chartData = Array.isArray(data)
        ? data
              .filter((d) => d.spo2 != null || d.heart_rate != null) // Filter out empty records
              .map((d, i, arr) => {
                  // Convert 24-hour time to 12-hour AM/PM format
                  const time24 = d.reading_time?.slice(-8) || "";
                  const time12 = time24
                      ? moment(time24, "HH:mm:ss").format("h:mm A")
                      : "";
                  return {
                      spo2: d.spo2 || 0,
                      Heart_Rate: d.heart_rate || 0,
                      time: time12,
                      time24: time24, // Keep original for sorting
                  };
              })
        : [];

    // X axis ticks: first, middle, last
    const xTicks =
        chartData.length > 2
            ? [
                  chartData[0].time,
                  chartData[Math.floor(chartData.length / 2)].time,
                  chartData[chartData.length - 1].time,
              ]
            : chartData.map((d) => d.time);

    // Custom dot renderer: only show dot for last data point, styled per line
    const customDot = (props) => {
        const { payload, cx, cy, stroke, dataKey, index } = props;
        if (payload === chartData[chartData.length - 1]) {
            // Style for spo2 line: filled circle
            if (dataKey === "spo2") {
                return (
                    <circle
                        key={`spo2-dot-${index}`}
                        cx={cx}
                        cy={cy}
                        r={7}
                        fill={stroke}
                        stroke={stroke}
                        strokeWidth={3}
                    />
                );
            }
            // Style for Heart_Rate (pulse) line: hollow circle
            if (dataKey === "Heart_Rate") {
                return (
                    <circle
                        key={`hr-dot-${index}`}
                        cx={cx}
                        cy={cy}
                        r={7}
                        fill="#fff"
                        stroke={stroke}
                        strokeWidth={3}
                    />
                );
            }
        }
        return null;
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart
                data={chartData}
                style={{ cursor: "pointer" }}
                margin={{ top: 5, right: 30, left: 30, bottom: 0 }}
            >
                <YAxis
                    yAxisId="pulse"
                    orientation="left"
                    domain={[40, 200]}
                    label={{
                        value: "Pulse Rate (bpm)",
                        angle: -90,
                        position: "insideLeft",
                        style: {
                            textAnchor: "middle",
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "14px",
                            color: "var(--primary-text-color)",
                        },
                    }}
                />
                <YAxis
                    yAxisId="spo2"
                    orientation="right"
                    domain={[70, 100]}
                    label={{
                        value: "SpO₂ (%)",
                        angle: 90,
                        position: "insideRight",
                        style: {
                            textAnchor: "middle",
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "14px",
                            color: "#444444",
                        },
                    }}
                />
                {/* Key grid lines for Pulse Rate (40-200) */}
                <ReferenceLine
                    y={10}
                    yAxisId="pulse"
                    stroke="#b0b0b0"
                    strokeWidth={0.5}
                />
                <ReferenceLine
                    y={45}
                    yAxisId="pulse"
                    stroke="#b0b0b0"
                    strokeWidth={0.5}
                />
                <ReferenceLine
                    y={80}
                    yAxisId="pulse"
                    stroke="#b0b0b0"
                    strokeWidth={0.5}
                />
                <ReferenceLine
                    y={120}
                    yAxisId="pulse"
                    stroke="#b0b0b0"
                    strokeWidth={0.5}
                />
                <ReferenceLine
                    y={160}
                    yAxisId="pulse"
                    stroke="#b0b0b0"
                    strokeWidth={0.5}
                />
                {/* Key grid lines for SpO2 (70-100) */}
                <ReferenceLine
                    y={80}
                    yAxisId="spo2"
                    stroke="#b0b0b0"
                    strokeWidth={0.5}
                />
                <ReferenceLine
                    y={90}
                    yAxisId="spo2"
                    stroke="#b0b0b0"
                    strokeWidth={0.5}
                />
                <ReferenceLine
                    y={85}
                    yAxisId="spo2"
                    stroke="#FFA500"
                    strokeWidth={2}
                />
                <ReferenceLine
                    y={92}
                    yAxisId="spo2"
                    stroke="#FFA500"
                    strokeWidth={2}
                />
                <Line
                    yAxisId="spo2"
                    stroke="rgb(128, 128, 128)"
                    strokeWidth={3}
                    activeDot={{ r: 8, fill: "rgb(49, 26, 26)" }}
                    type="monotone"
                    dataKey="spo2"
                    name="SpO₂"
                    dot={customDot}
                    strokeLinecap="round"
                    isAnimationActive={false}
                />
                <Line
                    yAxisId="pulse"
                    type="monotone"
                    dataKey="Heart_Rate"
                    name="Pulse Rate"
                    dot={customDot}
                    stroke="rgb(128, 128, 128)"
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    isAnimationActive={false}
                />
                <Tooltip />
                <XAxis dataKey="time" ticks={xTicks} />
            </LineChart>
        </ResponsiveContainer>
    );
}
